import * as React from 'react';
import { Library, Loader2, Pause, Play, Sparkles } from 'lucide-react';
import type { FillerStrategy, FillerCategory, FillerCategoryConfig } from '@cp3/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Label } from '../../../../components/ui/label';
import { Slider } from '../../../../components/ui/slider';
import { Switch } from '../../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { useFillers, useGenerateFillers, type FillerEntry } from '../../../../hooks/useFillers';
import { useToast } from '../../../../components/ui/toast';
import { cn } from '../../../../lib/utils';
import type { FormState } from '../state';
import { FillerLibraryDialog } from './FillerLibraryDialog';

interface Props {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}

const CATEGORY_LABELS: Record<FillerCategory, string> = {
  breath: 'Atemgeräusch',
  mhm: 'Mhm / Hmm',
  'filler-word': 'Füllwort (Ähm)',
  'wait-phrase': 'Warte-Phrase',
  'throat-clear': 'Räuspern',
  cough: 'Husten',
};

export function FillerTab({ form, setForm }: Props) {
  function patchCategory(idx: number, patch: Partial<FillerCategoryConfig>) {
    setForm((prev) => {
      const next = [...prev.fillerCategories];
      const current = next[idx];
      if (!current) return prev;
      next[idx] = { ...current, ...patch };
      return { ...prev, fillerCategories: next };
    });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Latenz-Optimierung</CardTitle>
          <CardDescription>
            Wenn das LLM länger als {form.fillerThresholdMs} ms zum Antworten braucht, spielt die Pipeline einen vorab-encodierten μ-law-Filler ab — das macht „totes Audio" unhörbar, ohne den Agent zu beschleunigen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <Label>Filler-Audio aktivieren</Label>
              <p className="text-xs text-muted-foreground">Master-Switch für die gesamte Filler-Pipeline.</p>
            </div>
            <Switch checked={form.fillerEnabled} onCheckedChange={(v) => setForm((p) => ({ ...p, fillerEnabled: v }))} />
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <Label>Immer Filler nach Kunden-Pause</Label>
              <p className="text-xs text-muted-foreground">
                Spielt sofort nach jedem „Kunde fertig" einen Filler ab — auch wenn das LLM schnell ist.
                Macht den Dialog flüssiger, kostet ~50 ms.
              </p>
            </div>
            <Switch
              checked={!!form.fillerAlwaysPlay}
              onCheckedChange={(v) => setForm((p) => ({ ...p, fillerAlwaysPlay: v }))}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Trigger-Schwelle: {form.fillerThresholdMs} ms</Label>
              <Slider
                value={[form.fillerThresholdMs]}
                min={300}
                max={2000}
                step={50}
                onValueChange={(v) => setForm((p) => ({ ...p, fillerThresholdMs: v[0] ?? p.fillerThresholdMs }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Strategie</Label>
              <Select
                value={form.fillerStrategy}
                onValueChange={(v) => setForm((p) => ({ ...p, fillerStrategy: v as FillerStrategy }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Zufällig (gewichtet)</SelectItem>
                  <SelectItem value="context-aware">Kontext-bewusst</SelectItem>
                  <SelectItem value="sequential">Sequenziell</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Max. Filler pro Anruf: {form.maxFillerPerCall}</Label>
              <Slider
                value={[form.maxFillerPerCall]}
                min={0}
                max={20}
                step={1}
                onValueChange={(v) => setForm((p) => ({ ...p, maxFillerPerCall: Math.round(v[0] ?? p.maxFillerPerCall) }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bibliothek pro Sprache</CardTitle>
          <CardDescription>
            Filler werden pro Sprache separat gespeichert — beim Wechsel der Anrufsprache bleiben
            die alten Aufnahmen erhalten. Klicke „Erzeugen" für eine Sprache, um den Default-Satz
            mit der aktuell ausgewählten Stimme zu generieren.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PerLanguageGrid voiceId={form.ttsVoiceId} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Kategorien & Audio-Bibliothek</CardTitle>
              <CardDescription>
                Pro Kategorie steuern, ob sie aktiv ist, wie viel Wartezeit nötig ist und wie oft
                sie pro Anruf vorkommen darf. Klicke „Audio verwalten", um konkrete Filler-Sounds
                zu generieren oder zu prüfen.
              </CardDescription>
            </div>
            <FillerStatsBadge />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">Kategorie</th>
                  <th className="px-2 py-2">Aktiv</th>
                  <th className="px-2 py-2">Min-Delay (ms)</th>
                  <th className="px-2 py-2">Max/Anruf</th>
                  <th className="px-2 py-2">Audios</th>
                </tr>
              </thead>
              <tbody>
                {form.fillerCategories.map((c, idx) => (
                  <FillerCategoryRow
                    key={c.category}
                    config={c}
                    label={CATEGORY_LABELS[c.category]}
                    voiceId={form.ttsVoiceId}
                    language={form.language}
                    onPatch={(patch) => patchCategory(idx, patch)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end">
            <FillerLibraryDialog
              voiceId={form.ttsVoiceId}
              language={form.language}
              trigger={
                <Button variant="outline" size="sm">
                  <Library className="mr-2 h-4 w-4" /> Komplette Bibliothek verwalten
                </Button>
              }
            />
          </div>

          <div className="mt-6 border-t border-border pt-4">
            <FillerAudioInline currentLanguage={form.language} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FillerAudioInline({ currentLanguage }: { currentLanguage: string }) {
  const fillers = useFillers();
  const [playingId, setPlayingId] = React.useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [filterLang, setFilterLang] = React.useState<'current' | 'all'>('current');

  React.useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const items = React.useMemo(() => {
    const list = fillers.data ?? [];
    if (filterLang === 'all') return list;
    const root = (currentLanguage || '').toLowerCase().split('-')[0];
    return list.filter((f) => {
      const l = (f.language ?? '').toLowerCase();
      return !l || l === currentLanguage.toLowerCase() || l.startsWith(root + '-') || l === root;
    });
  }, [fillers.data, currentLanguage, filterLang]);

  const grouped = React.useMemo(() => {
    const map: Record<string, FillerEntry[]> = {};
    for (const f of items) {
      (map[f.category] ??= []).push(f);
    }
    return map;
  }, [items]);

  function toggle(item: FillerEntry) {
    if (playingId === item.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (!item.previewUrl) return;
    audioRef.current?.pause();
    const audio = new Audio(item.previewUrl);
    audioRef.current = audio;
    setPlayingId(item.id);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => setPlayingId(null);
    void audio.play().catch(() => setPlayingId(null));
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">Audio-Vorhören</div>
          <p className="text-xs text-muted-foreground">
            Alle generierten Filler-Aufnahmen — klick zum Abspielen.
          </p>
        </div>
        <Select value={filterLang} onValueChange={(v) => setFilterLang(v as 'current' | 'all')}>
          <SelectTrigger className="h-8 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current">Aktive Sprache</SelectItem>
            <SelectItem value="all">Alle Sprachen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {fillers.isLoading ? (
        <div className="flex h-24 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
          Noch keine Filler generiert. Klick „Erzeugen" bei der jeweiligen Sprache oben.
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {CATEGORY_LABELS[cat as FillerCategory] ?? cat}
              </div>
              <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggle(item)}
                    disabled={!item.previewUrl}
                    className={cn(
                      'flex items-center gap-2 rounded-md border border-border p-2 text-left text-xs transition-colors',
                      playingId === item.id
                        ? 'border-primary bg-primary/10'
                        : 'hover:bg-secondary',
                      !item.previewUrl && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      {playingId === item.id ? (
                        <Pause className="h-3 w-3" />
                      ) : (
                        <Play className="h-3 w-3" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{item.label}</div>
                      <div className="truncate text-[10px] text-muted-foreground font-mono">
                        {item.language ?? '—'} · {item.durationMs ?? 0}ms · w{(item.weight ?? 0).toFixed(2)}
                        {!item.active && ' · inaktiv'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FillerCategoryRow({
  config,
  label,
  voiceId,
  language,
  onPatch,
}: {
  config: FillerCategoryConfig;
  label: string;
  voiceId: string;
  language: string;
  onPatch: (patch: Partial<FillerCategoryConfig>) => void;
}) {
  const fillers = useFillers();
  const count = (fillers.data ?? []).filter((f) => f.category === config.category).length;
  return (
    <tr className="border-b border-border/50 last:border-0">
      <td className="px-2 py-2 font-medium">{label}</td>
      <td className="px-2 py-2">
        <Switch checked={config.enabled} onCheckedChange={(v) => onPatch({ enabled: v })} />
      </td>
      <td className="px-2 py-2">
        <Input
          type="number"
          min={0}
          max={10000}
          step={100}
          value={config.minDelayMs}
          onChange={(e) => onPatch({ minDelayMs: Number(e.target.value) })}
          className="h-8 w-24"
        />
      </td>
      <td className="px-2 py-2">
        <Input
          type="number"
          min={0}
          max={20}
          value={config.maxPerCall}
          onChange={(e) => onPatch({ maxPerCall: Number(e.target.value) })}
          className="h-8 w-20"
        />
      </td>
      <td className="px-2 py-2">
        <FillerLibraryDialog
          category={config.category}
          voiceId={voiceId}
          language={language}
          trigger={
            <Button variant="ghost" size="sm" className="h-8">
              {count} <Library className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          }
        />
      </td>
    </tr>
  );
}

function FillerStatsBadge() {
  const fillers = useFillers();
  const total = fillers.data?.length ?? 0;
  const active = (fillers.data ?? []).filter((f) => f.active).length;
  return (
    <div className="text-right text-xs text-muted-foreground">
      <div>{total} Audios in Bibliothek</div>
      <div>{active} aktiv</div>
    </div>
  );
}

const SUPPORTED_LANGUAGES: ReadonlyArray<{ code: 'de-DE' | 'de-AT' | 'en-US'; label: string }> = [
  { code: 'de-DE', label: 'Deutsch (DE)' },
  { code: 'de-AT', label: 'Deutsch (AT)' },
  { code: 'en-US', label: 'Englisch (US)' },
];

function PerLanguageGrid({ voiceId }: { voiceId: string }) {
  const fillers = useFillers();
  const generate = useGenerateFillers();
  const { toast } = useToast();
  const [busyLang, setBusyLang] = React.useState<string | null>(null);

  async function regen(language: 'de-DE' | 'de-AT' | 'en-US') {
    setBusyLang(language);
    try {
      // force=true so a re-click refreshes existing entries with the
      // currently-selected voice + the latest audio format. Users
      // expect "Erzeugen" to actually produce new audio, not skip.
      const r = await generate.mutateAsync({ voiceId, language, force: true });
      toast({
        title: 'Filler generiert',
        description: `${r.written.length} neu erzeugt.`,
      });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setBusyLang(null);
    }
  }

  const byLang = React.useMemo(() => {
    const map: Record<string, number> = {};
    for (const f of fillers.data ?? []) {
      const k = (f.language ?? '').toString();
      map[k] = (map[k] ?? 0) + 1;
    }
    return map;
  }, [fillers.data]);

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {SUPPORTED_LANGUAGES.map((l) => {
        const count = byLang[l.code] ?? 0;
        const busy = busyLang === l.code;
        return (
          <div
            key={l.code}
            className="flex items-center justify-between rounded-md border border-border p-3"
          >
            <div>
              <div className="text-sm font-medium">{l.label}</div>
              <div className="text-xs text-muted-foreground">{count} Audios</div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void regen(l.code)}
              disabled={busy || !voiceId}
            >
              {busy ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              )}
              Erzeugen
            </Button>
          </div>
        );
      })}
    </div>
  );
}
